import { ComputedRef, ref, Ref } from 'vue';
import { useDialogFocus } from '@ilteducation/components';
import type { ContentReference } from '@ilteducation/content-collections-types';
import useSnackbar from '@/composables/use-snackbar';
import { useActivityStream } from '@/composables/use-activity-stream';
import { useLocale } from '@/plugins/translation';
import { useRoute } from 'vue-router';

type Options = {
  contentReference: ComputedRef<ContentReference>;
  context: string;
  extraDialogRefs?: Ref<boolean>[];
  onUpdated?: (name: string) => void;
  onCreated?: (name: string) => void;
};

export function useAddToCollection(options: Options) {
  const { snackbarSuccess } = useSnackbar();
  const { logAddContentToCollection, logCreateCollection } = useActivityStream();
  const { t, l } = useLocale();
  const route = useRoute();

  const showSelectDialog = ref(false);
  const showCreateDialog = ref(false);

  const { rememberFocus } = useDialogFocus([showSelectDialog, showCreateDialog, ...(options.extraDialogRefs ?? [])]);

  const openSelectDialog = () => {
    rememberFocus();
    showSelectDialog.value = true;
  };

  const closeSelectDialog = () => {
    showSelectDialog.value = false;
  };

  const openCreateDialog = () => {
    rememberFocus();
    showCreateDialog.value = true;
    showSelectDialog.value = false;
  };

  const closeCreateDialog = () => {
    showCreateDialog.value = false;
  };

  const defaultOnUpdated = (name: string) => {
    logAddContentToCollection(route.fullPath);
    snackbarSuccess(t(l.content_collections.message_added_to_collection, { collection: name }));
  };

  const defaultOnCreated = (name: string) => {
    snackbarSuccess(t(l.content_collections.message_added_to_collection, { collection: name }));
    logCreateCollection(route.fullPath);
  };

  const handleUpdateCollection = (id: string, name: string) => {
    closeSelectDialog();
    defaultOnUpdated(name);
    options.onUpdated?.(name);
  };

  const handleCreateCollection = (id: string, name: string) => {
    closeCreateDialog();
    defaultOnCreated(name);
    options.onCreated?.(name);
  };

  return {
    showSelectDialog,
    showCreateDialog,
    openSelectDialog,
    closeSelectDialog,
    openCreateDialog,
    closeCreateDialog,
    handleUpdateCollection,
    handleCreateCollection,
    rememberFocus,
    contentReference: options.contentReference,
    context: options.context,
  };
}
